// Stream epochs/cursors order transient events; durable command IDs and game
// revisions survive reconnect/reload independently of that stream.

import { PendingGameCommand, commandMessage } from "./commands.ts";
import { clientInstance, socketLimitMessage } from "../clientInstance.ts";

import type { ClientMsg, GameAction, ServerEnvelope } from "./types.ts";

export interface SocketHandlers {
  onEnvelope: (env: ServerEnvelope) => void;
  onStatus: (status: "live" | "reconnecting") => void;
  onRefused?: (message: string, retry: () => void) => void;
  onPending?: (pending: boolean) => void;
  onAvailability?: (available: boolean) => void;
}

const PING_EVERY_MS = 3000;
const MOVE_RESEND_MS = 1200;
const RECONNECT_BASE_MS = 500;
const RECONNECT_MAX_MS = 8000;

export class GameSocket {
  /** Last applied server version (sent as `?v=` on reconnect). */
  version = 0;
  /** Measured one-way lag, centis — attached to outgoing moves as `l`. */
  lagCs = 0;

  private ws?: WebSocket;
  private closed = false;
  private reconnectAttempts = 0;
  private pingTimer?: number;
  private pingSentAt = 0;

  private commands: PendingGameCommand;
  private resendTimer?: number;
  private epoch?: string;
  private available = false;
  private awaitingInitial = true;
  private needsRecovery = true;
  private reconnectTimer?: number;

  constructor(
    private gameId: string,
    accountId: string,
    private handlers: SocketHandlers,
  ) {
    try {
      this.commands = new PendingGameCommand(accountId, gameId, sessionStorage);
    } catch {
      // The board remains readable when browser storage is disabled. Mutations
      // fail before send because a retry identity could not be saved.
      this.commands = new PendingGameCommand(accountId, gameId, {
        getItem: () => null, removeItem: () => {}, setItem: () => { throw new Error("storage unavailable"); },
      });
    }
  }

  get hasPending(): boolean { return this.commands.pending !== undefined; }

  connect(): void {
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.closed = false;
    this.awaitingInitial = true;
    this.needsRecovery = true;
    this.available = false;
    this.publishAvailability();
    const proto = location.protocol === "https:" ? "wss" : "ws";
    const url = `${proto}://${location.host}/ws/game/${encodeURIComponent(this.gameId)}?protocol=2&client_id=${clientInstance()}&v=0`;
    const ws = new WebSocket(url);
    this.ws = ws;

    ws.onopen = () => {
      if (this.closed || this.ws !== ws) return;
      this.reconnectAttempts = 0;
      this.handlers.onStatus("live");
      this.startPing();
      // Re-arm a resend for any move that was in flight across the reconnect.
      if (this.hasPending) this.armResend();
    };

    ws.onmessage = (ev) => {
      if (this.closed || this.ws !== ws) return;
      let env: ServerEnvelope;
      try {
        env = JSON.parse(ev.data as string) as ServerEnvelope;
      } catch {
        return;
      }
      this.intercept(env);
    };

    ws.onclose = (event) => {
      if (this.closed || this.ws !== ws) return;
      this.stopPing();
      this.available = false;
      this.publishAvailability();
      const notice = socketLimitMessage(event?.code);
      if (notice) {
        this.closed = true;
        if (this.resendTimer) clearInterval(this.resendTimer);
        this.handlers.onStatus("reconnecting");
        this.handlers.onEnvelope({ t: "error", d: notice });
        this.handlers.onRefused?.(notice, () => this.connect());
      }
      if (!this.closed) this.scheduleReconnect();
    };
    ws.onerror = () => ws.close();
  }

  close(): void {
    this.closed = true;
    this.available = false;
    this.publishAvailability();
    this.stopPing();
    if (this.resendTimer) clearInterval(this.resendTimer);
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    // Keep pending storage: closing/navigation is not an authoritative outcome.
    this.ws?.close();
  }

  // --- outgoing --------------------------------------------------------

  private raw(msg: ClientMsg): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(msg));
    }
  }

  private command(action: GameAction): boolean {
    if (this.awaitingInitial || !this.available || this.hasPending) return false;
    try {
      if (!this.commands.begin(action)) return false;
    } catch {
      this.handlers.onEnvelope({ t: "error", d: "Your action could not be saved for reconnect. Please try again." });
      return false;
    }
    this.handlers.onPending?.(true);
    this.publishAvailability();
    this.sendPending();
    this.armResend();
    return true;
  }

  sendMove(uci: string, thinkMs: number): boolean {
    return this.command({ type: "move", uci,
      lag_cs: Math.min(60_000, Math.max(0, Math.round(this.lagCs))),
      think_time: Math.min(214_748_364, Math.max(0, Math.round(thinkMs / 100))).toString(36) });
  }

  resign(): void { this.command({ type: "resign" }); }
  offerOrAcceptDraw(): void { this.command({ type: "draw_yes" }); }
  declineDraw(): void { this.command({ type: "draw_no" }); }
  claimFlag(_color: "white" | "black"): void { this.command({ type: "flag" }); }
  claimWin(): void { this.command({ type: "claim_win" }); }
  pause(): void { this.command({ type: "pause" }); }
  resumeReady(): void { this.command({ type: "resume_ready" }); }
  withdrawResume(): void { this.command({ type: "withdraw_resume" }); }
  rematch(): void {
    this.command({ type: "rematch_ready" });
  }
  withdrawRematch(): void { this.command({ type: "withdraw_rematch" }); }
  chat(text: string): void {
    this.raw({ t: "chat", d: text });
  }

  private sendPending(): void {
    if (!this.awaitingInitial && this.commands.pending) this.raw({ t: "command", d: this.commands.pending });
  }
  private publishAvailability(): void {
    this.handlers.onAvailability?.(!this.awaitingInitial && this.available && !this.hasPending);
  }
  private armResend(): void {
    if (!this.hasPending) return;
    if (this.resendTimer) clearInterval(this.resendTimer);
    this.resendTimer = window.setInterval(() => this.sendPending(), MOVE_RESEND_MS);
  }

  // --- incoming: version + ack handling --------------------------------

  private intercept(env: ServerEnvelope): void {
    switch (env.t) {
      case "command-result":
        if (env.d.outcome === "unknown") {
          this.handlers.onEnvelope({ t: "error", d: "Checking whether your action was saved…" });
          return;
        }
        let resolved = false;
        try { resolved = this.commands.resolve(env.d); }
        catch {
          // Keep retrying an already resolved receipt if local storage is
          // temporarily unwritable; never lose the retry identity on reload.
          this.handlers.onEnvelope({ t: "error", d: "Your action was checked, but reconnect storage is unavailable. Please try again." });
        }
        if (resolved) {
          if (this.resendTimer) clearInterval(this.resendTimer);
          this.handlers.onPending?.(false);
          this.publishAvailability();
          if (env.d.outcome === "rejected") this.handlers.onEnvelope({ t: "error", d: commandMessage(env.d.code) });
        }
        return;
      case "ack":
        // Numeric legacy acks never settle a durable command.
        return;
      case "pong":
        this.lagCs = Math.max(0, ((performance.now() - this.pingSentAt) / 2) * 0.1);
        return;
      case "batch":
        for (const inner of env.d) this.intercept(inner);
        return;
      case "reload":
        // Buffer too old to replay: drop our version and reconnect for a full.
        this.version = 0;
        this.reconnect();
        return;
      case "resync":
        this.awaitingInitial = true;
        this.publishAvailability();
        this.raw({ t: "resync" });
        return;
      case "full":
        // Authoritative reset: adopt its version and hand it up.
        if (typeof env.v === "number") this.version = env.v;
        this.epoch = env.d.stream_epoch ?? undefined;
        this.available = env.d.protocol_version === 2 && env.d.mutations_available === true;
        if (typeof env.d.state_revision === "number") this.commands.observe(env.d.state_revision);
        this.handlers.onEnvelope(env);
        this.handlers.onPending?.(this.hasPending);
        const initial = this.needsRecovery;
        this.needsRecovery = false;
        this.awaitingInitial = false;
        this.publishAvailability();
        if (this.hasPending) {
          if (initial) this.sendPending();
          this.armResend();
        }
        return;
    }

    if (env.epoch && env.epoch !== this.epoch) {
      this.awaitingInitial = true;
      this.publishAvailability();
      this.raw({ t: "resync" });
      return;
    }
    // Versioned events: dedupe old, apply the next, resync on a gap.
    const v = (env as { v?: number }).v;
    if (typeof v === "number") {
      if (v <= this.version) return; // already applied
      if (v > this.version + 1) {
        this.awaitingInitial = true;
        this.publishAvailability();
        this.raw({ t: "resync" }); // gap → ask for a fresh full
        return;
      }
      this.version = v;
    }
    if (env.t === "move" || env.t === "end" || env.t === "draw-offer") this.commands.observe(env.d.state_revision);
    this.handlers.onEnvelope(env);
  }

  // --- keepalive + reconnect -------------------------------------------

  private startPing(): void {
    this.stopPing();
    this.pingTimer = window.setInterval(() => {
      this.pingSentAt = performance.now();
      this.raw({ t: "ping" });
      this.raw({ t: "activity", d: { foreground: document.visibilityState === "visible" } });
    }, PING_EVERY_MS);
  }
  private stopPing(): void {
    if (this.pingTimer) clearInterval(this.pingTimer);
    this.pingTimer = undefined;
  }

  private reconnect(): void {
    this.ws?.close();
  }
  private scheduleReconnect(): void {
    this.handlers.onStatus("reconnecting");
    const delay = Math.min(RECONNECT_MAX_MS, RECONNECT_BASE_MS * 2 ** this.reconnectAttempts);
    this.reconnectAttempts++;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = window.setTimeout(() => {
      if (!this.closed) this.connect();
    }, delay);
  }
}
